import React, { useEffect, useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import styles from "./ConnectionsPage.module.scss";
import { Trash } from "@phosphor-icons/react";
import LoadingOverlay from "../components/LoadingOverlay/LoadingOverlay";
import TabbedFormLayout from "../components/TabbedFormLayout/TabbedFormLayout";
import { useAuth } from "../context/AuthContext";

interface OrgConnection {
  id: number;
  org_id_one: string;
  org_id_two: string;
  org_one_name: string;
  org_two_name: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface UserConnection {
  id: number;
  user_id_one: string;
  user_id_two: string;
  user_one_firstname: string;
  user_one_lastname: string;
  user_two_firstname: string;
  user_two_lastname: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface AuthContext {
  user: { id: string; issuperadmin?: boolean; activeorganizationid?: string; firstname?: string; lastname?: string; app_name?: string } | null;
}

const CONNECTIONS_PER_PAGE = 10;

export default function ConnectionsPage() {
  const [orgConnections, setOrgConnections] = useState<OrgConnection[]>([]);
  const [userConnections, setUserConnections] = useState<UserConnection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<'org' | 'user'>('org');
  const [orgSearchQuery, setOrgSearchQuery] = useState<string>("");
  const [userSearchQuery, setUserSearchQuery] = useState<string>("");
  const [orgCurrentPage, setOrgCurrentPage] = useState<number>(1);
  const [userCurrentPage, setUserCurrentPage] = useState<number>(1);
  const [confirmDeleteOrgConnection, setConfirmDeleteOrgConnection] = useState<OrgConnection | null>(null);
  const [confirmDeleteUserConnection, setConfirmDeleteUserConnection] = useState<UserConnection | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user } = useAuth() as AuthContext;

  useEffect(() => {
    console.log("[ConnectionsPage] User:", user);
    console.log("[ConnectionsPage] Initial navigate reference:", navigate);
    const checkAuth = async () => {
      try {
        const res = await api.get<{ user: any }>('/auth/me');
        console.log("[ConnectionsPage] /auth/me Response:", res.data);
      } catch (err) {
        console.error("[ConnectionsPage] /auth/me Error:", err);
      }
    };
    checkAuth();
  }, [user, navigate]);

  const fetchOrgConnections = async () => {
    try {
      setError("");
      const res = await api.post<{ message: string; connections: OrgConnection[] }>("/core/connections/org/get");
      console.log("[fetchOrgConnections] Response:", res.data);
      setOrgConnections(res.data.connections || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to fetch organization connections";
      console.error("[fetchOrgConnections] Error:", err);
      setError(errorMessage);
    }
  };

  const fetchUserConnections = async () => {
    try {
      console.log("[ConnectionsPage] Getting user connections");
      setError("");
      const res = await api.post<{ message: string; connections: UserConnection[] }>("/core/connections/user/get");
      console.log("[fetchUserConnections] Response:", res.data);
      setUserConnections(res.data.connections || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to fetch user connections";
      console.error("[fetchUserConnections] Error:", err);
      setError(errorMessage);
    }
  };

  useEffect(() => {
    if (!user) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOrgConnections(),
        fetchUserConnections(), // Removed issuperadmin check
      ]);
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const handleOrgSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setOrgSearchQuery(e.target.value);
    setOrgCurrentPage(1);
  };

  const handleUserSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setUserSearchQuery(e.target.value);
    setUserCurrentPage(1);
  };

  const filteredOrgConnections = orgConnections.filter(
    (conn) =>
      conn.org_one_name.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
      conn.org_two_name.toLowerCase().includes(orgSearchQuery.toLowerCase())
  );

  const filteredUserConnections = userConnections.filter(
    (conn) =>
      `${conn.user_one_firstname} ${conn.user_one_lastname}`.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      `${conn.user_two_firstname} ${conn.user_two_lastname}`.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  const paginatedOrgConnections = filteredOrgConnections.slice(
    (orgCurrentPage - 1) * CONNECTIONS_PER_PAGE,
    orgCurrentPage * CONNECTIONS_PER_PAGE
  );

  const paginatedUserConnections = filteredUserConnections.slice(
    (userCurrentPage - 1) * CONNECTIONS_PER_PAGE,
    userCurrentPage * CONNECTIONS_PER_PAGE
  );

  const orgTotalPages = Math.ceil(filteredOrgConnections.length / CONNECTIONS_PER_PAGE);
  const userTotalPages = Math.ceil(filteredUserConnections.length / CONNECTIONS_PER_PAGE);

  const handleDeleteOrgConnection = (conn: OrgConnection) => {
    console.log("[ConnectionsPage] Opening delete modal for org connection:", conn.id);
    setConfirmDeleteOrgConnection(conn);
    setConfirmDeleteUserConnection(null);
    setShowDeleteModal(true);
  };

  const handleDeleteUserConnection = (conn: UserConnection) => {
    console.log("[ConnectionsPage] Opening delete modal for user connection:", conn.id);
    setConfirmDeleteUserConnection(conn);
    setConfirmDeleteOrgConnection(null);
    setShowDeleteModal(true);
  };

  const handleCancel = () => {
    console.log("[ConnectionsPage] Cancel button clicked");
    try {
      setShowDeleteModal(false);
      console.log("[ConnectionsPage] Navigating to /dashboard");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("[ConnectionsPage] Navigation error:", err);
    }
  };

  const confirmDelete = async () => {
    try {
      if (confirmDeleteOrgConnection) {
        console.log("[ConnectionsPage] Deleting org connection:", confirmDeleteOrgConnection.id);
        await api.post("/core/connections/org/delete", {
          org_id_one: confirmDeleteOrgConnection.org_id_one,
          org_id_two: confirmDeleteOrgConnection.org_id_two,
        });
        await fetchOrgConnections();
      } else if (confirmDeleteUserConnection) {
        console.log("[ConnectionsPage] Deleting user connection:", confirmDeleteUserConnection.id);
        await api.post("/core/connections/user/delete", {
          user_id_one: confirmDeleteUserConnection.user_id_one,
          user_id_two: confirmDeleteUserConnection.user_id_two,
        });
        await fetchUserConnections();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to delete connection";
      console.error("[confirmDelete] Error:", err);
      setError(errorMessage);
    } finally {
      setShowDeleteModal(false);
      setConfirmDeleteOrgConnection(null);
      setConfirmDeleteUserConnection(null);
    }
  };

  const handleUpdateStatus = async (conn: OrgConnection | UserConnection, type: 'org' | 'user', status: 'approved' | 'rejected') => {
    try {
      if (type === 'org') {
        console.log("[ConnectionsPage] Updating org connection status:", conn.id, status);
        await api.post("/core/connections/org/update", {
          org_id_one: (conn as OrgConnection).org_id_one,
          org_id_two: (conn as OrgConnection).org_id_two,
          status,
        });
        await fetchOrgConnections();
      } else {
        console.log("[ConnectionsPage] Updating user connection status:", conn.id, status);
        await api.post("/core/connections/user/update", {
          user_id_one: (conn as UserConnection).user_id_one,
          user_id_two: (conn as UserConnection).user_id_two,
          status,
        });
        await fetchUserConnections();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to update connection status";
      console.error("[handleUpdateStatus] Error:", err);
      setError(errorMessage);
    }
  };

  const tabs = [
    {
      label: "Organization Connections",
      value: "org",
      content: (
        <div className={styles.tabContent}>
          <div className={styles.header}>
            <button onClick={() => navigate("/connections/new-org-connection")} className={styles.actionButton}>
              + Add Organization Connection
            </button>
            <input
              type="text"
              placeholder="Search organizations..."
              value={orgSearchQuery}
              onChange={handleOrgSearch}
              className={styles.searchInput}
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Org ID One</th>
                <th>Org Name One</th>
                <th>Org ID Two</th>
                <th>Org Name Two</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrgConnections.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center' }}>
                    No organization connections found.
                  </td>
                </tr>
              ) : (
                paginatedOrgConnections.map((conn) => (
                  <tr key={conn.id}>
                    <td>{conn.org_id_one}</td>
                    <td>{conn.org_one_name}</td>
                    <td>{conn.org_id_two}</td>
                    <td>{conn.org_two_name}</td>
                    <td>{conn.status}</td>
                    <td>{new Date(conn.created_at).toLocaleString()}</td>
                    <td className={styles.actions}>
                      <button
                        onClick={() => handleUpdateStatus(conn, 'org', 'approved')}
                        className={styles.actionButton}
                        title="Approve"
                        disabled={conn.status === 'approved'}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(conn, 'org', 'rejected')}
                        className={styles.actionButton}
                        title="Reject"
                        disabled={conn.status === 'rejected'}
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleDeleteOrgConnection(conn)}
                        className={styles.deleteButton}
                        title="Delete"
                      >
                        <Trash size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {orgTotalPages > 1 && (
            <div className={styles.pagination}>
              {Array.from({ length: orgTotalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setOrgCurrentPage(num)}
                  className={num === orgCurrentPage ? styles.activePage : ""}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "User Connections",
      value: "user",
      content: (
        <div className={styles.tabContent}>
          <div className={styles.header}>
            <button onClick={() => navigate("/connections/new-user-connection")} className={styles.actionButton}>
              + Add User Connection
            </button>
            <input
              type="text"
              placeholder="Search users..."
              value={userSearchQuery}
              onChange={handleUserSearch}
              className={styles.searchInput}
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User ID One</th>
                <th>User Name One</th>
                <th>User ID Two</th>
                <th>User Name Two</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUserConnections.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center' }}>
                    No user connections found.
                  </td>
                </tr>
              ) : (
                paginatedUserConnections.map((conn) => (
                  <tr key={conn.id}>
                    <td>{conn.user_id_one}</td>
                    <td>{`${conn.user_one_firstname} ${conn.user_one_lastname}`}</td>
                    <td>{conn.user_id_two}</td>
                    <td>{`${conn.user_two_firstname} ${conn.user_two_lastname}`}</td>
                    <td>{conn.status}</td>
                    <td>{new Date(conn.created_at).toLocaleString()}</td>
                    <td className={styles.actions}>
                      <button
                        onClick={() => handleUpdateStatus(conn, 'user', 'approved')}
                        className={styles.actionButton}
                        title="Approve"
                        disabled={conn.status === 'approved'}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(conn, 'user', 'rejected')}
                        className={styles.actionButton}
                        title="Reject"
                        disabled={conn.status === 'rejected'}
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleDeleteUserConnection(conn)}
                        className={styles.deleteButton}
                        title="Delete"
                      >
                        <Trash size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {userTotalPages > 1 && (
            <div className={styles.pagination}>
              {Array.from({ length: userTotalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setUserCurrentPage(num)}
                  className={num === userCurrentPage ? styles.activePage : ""}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {loading && <LoadingOverlay />}
      <TabbedFormLayout tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} showActions={false} />
      {showDeleteModal && (confirmDeleteOrgConnection || confirmDeleteUserConnection) && (
        <div className={styles.modalBackdrop} onClick={() => console.log("[ConnectionsPage] Modal backdrop clicked")}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p>
              Are you sure you want to delete this connection between{" "}
              <strong>
                {confirmDeleteOrgConnection
                  ? `${confirmDeleteOrgConnection.org_one_name} and ${confirmDeleteOrgConnection.org_two_name}`
                  : `${confirmDeleteUserConnection?.user_one_firstname} ${confirmDeleteUserConnection?.user_one_lastname} and ${confirmDeleteUserConnection?.user_two_firstname} ${confirmDeleteUserConnection?.user_two_lastname}`}
              </strong>?
            </p>
            <div className={styles.modalActions}>
              <button onClick={confirmDelete} className={styles.confirmButton}>Yes, Delete</button>
              <button
                onClick={() => {
                  console.log("[ConnectionsPage] Cancel button clicked");
                  try {
                    setShowDeleteModal(false);
                    console.log("[ConnectionsPage] Navigating to /dashboard");
                    navigate("/dashboard", { replace: true });
                  } catch (err) {
                    console.error("[ConnectionsPage] Navigation error:", err);
                  }
                }}
                onMouseDown={() => console.log("[ConnectionsPage] Cancel button mouse down")}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
